(function () {
  // VAT Validator integration (observes third-party VAT form, emits events, and posts success to API)
  // Config expected at window.VATValidatorConfig -> { apiEndpoint, shopCustomerEmail }

  const CONFIG = window.VATValidatorConfig || {};
  const VAT_API_ENDPOINT =
    CONFIG.apiEndpoint || 'https://nets4you-stock-node-api-production.onrender.com/api/v1/shopify-customer/create';
  const SHOP_CUSTOMER_EMAIL = CONFIG.shopCustomerEmail || null;

  const callbacks = new Set();
  let lastState = null;

  // Loader reference counting to avoid flicker when multiple processes request/hide loader
  if (typeof window._vatValidatorLoaderCount === 'undefined') window._vatValidatorLoaderCount = 0;
  function showLoader() {
    try {
      if (window.externalApiClient && typeof window.externalApiClient._showLoader === 'function') {
        if (window._vatValidatorLoaderCount++ === 0) window.externalApiClient._showLoader();
      }
    } catch (e) {
      console.warn('VATValidator showLoader error', e);
    }
  }
  function hideLoader() {
    try {
      if (window.externalApiClient && typeof window.externalApiClient._hideLoader === 'function') {
        window._vatValidatorLoaderCount = Math.max(0, (window._vatValidatorLoaderCount || 0) - 1);
        if (window._vatValidatorLoaderCount === 0) window.externalApiClient._hideLoader();
      }
    } catch (e) {
      console.warn('VATValidator hideLoader error', e);
    }
  }

  function getVatForm() {
    return document.querySelector('.f-vat-validator-form');
  }

  function getStateFromDom(form) {
    if (!form) return 'absent';
    const success = form.querySelector('.banner.success');
    const error = form.querySelector('.banner.error');
    const button = form.querySelector('button');

    const isSuccess = success && window.getComputedStyle(success).display !== 'none';
    const isError = error && window.getComputedStyle(error).display !== 'none';
    const isValidating = button && (button.classList.contains('loading') || button.disabled);

    if (isSuccess) return 'success';
    if (isError) return 'error';
    if (isValidating) return 'validating';
    return 'normal';
  }

  // Banner helpers to avoid duplicated DOM-manipulation code
  function showSuccessBanner(form) {
    if (!form) return;
    const successBanner = form.querySelector('.banner.success');
    if (!successBanner) return;
    const extra = ' Please use the same email on checkout page for VAT exemption.';
    if (!successBanner.dataset.vatOriginalText)
      successBanner.dataset.vatOriginalText = (successBanner.textContent || '').trim();
    const original = (successBanner.dataset.vatOriginalText || '').trim();
    if (original) {
      if (!successBanner.textContent.includes(extra)) successBanner.textContent = original + extra;
    } else {
      if (!successBanner.textContent.includes(extra))
        successBanner.textContent = 'VAT number validated successfully!' + extra;
    }
    successBanner.style.display = 'block';
    successBanner.style.fontSize = '1.2rem';
    const errorBanner = form.querySelector('.banner.error');
    if (errorBanner) errorBanner.style.display = 'none';
  }

  function showErrorBanner(form, message) {
    if (!form) return;
    const errorBanner = form.querySelector('.banner.error');
    if (errorBanner) {
      errorBanner.textContent = message || 'An error occurred';
      errorBanner.style.display = 'block';
      const successBanner = form.querySelector('.banner.success');
      if (successBanner) successBanner.style.display = 'none';
    } else {
      console.warn('VATValidator: no error banner available to show message');
    }
  }

  function emit(state, form) {
    // Suppression guard: when we programmatically update banners (showSuccessBanner / showErrorBanner)
    // the MutationObserver detects the DOM change and would re-emit the same state, which can cause
    // an infinite loop of API calls. To prevent that, callers set `form._vatValidatorSuppressNextEmit = true`
    // before making a programmatic update; here we clear the flag and skip the next emit.
    if (form && form._vatValidatorSuppressNextEmit) {
      form._vatValidatorSuppressNextEmit = false;
      return;
    }

    if (state === lastState) return;
    lastState = state;
    const vatNumber = (form && form.querySelector('input[name="vat_number"]')?.value) || null;
    const email = (form && form.querySelector('input[name="vat_email"]')?.value) || null;
    const detail = { state, vatNumber, email };

    document.dispatchEvent(new CustomEvent('vat:validation', { detail }));
    console.info('VATValidator: dispatched event', detail);

    callbacks.forEach((cb) => {
      try {
        cb(detail);
      } catch (e) {
        console.error('VATValidator callback error', e);
      }
    });

    // Loader handling: show while validating. For success we defer hiding/showing until
    // our backend API confirms the registration; on error hide loader immediately.
    if (state === 'validating') {
      showLoader();
    }

    if (state === 'error') {
      hideLoader();
    }

    // When third-party validation reports success, call our API (once per vat+email) and
    // show the visible success banner only after our API returns success.
    if (state === 'success') {
      console.info('VATValidator: success state detected', { vatNumber, email });

      const successBanner = form && form.querySelector('.banner.success');
      // Hide any third-party success banner until backend confirms
      if (successBanner) {
        successBanner.style.display = 'none';
      }

      if (vatNumber) {
        // Always call backend when third-party validation reports success
        const initiated = sendSuccessToApi(detail, form);
        if (!initiated) {
          // API call was skipped (e.g., missing email) - show success immediately
          if (form) {
            // Prevent the programmatic banner update from triggering a follow-up emit
            form._vatValidatorSuppressNextEmit = true;
            lastState = 'success';
          }
          showSuccessBanner(form);
          hideLoader();
        }
      } else {
        console.debug('VATValidator: success reported but no VAT present', vatNumber, email);
      }
    }
  }

  // Debounce utility
  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  function observeForm(form) {
    if (!form) return;
    if (form._vatValidatorInitialized) return;
    form._vatValidatorInitialized = true;
    console.info('VATValidator: observing form', form);

    // Ensure an email input exists (to collect dynamic email similar to VAT number)
    try {
      if (!form.querySelector('input[name="vat_email"]')) {
        const vatInputWrapper = form.querySelector('.input-wrapper');
        const emailWrapper = document.createElement('div');
        emailWrapper.className = 'input-wrapper';
        const emailInput = document.createElement('input');
        emailInput.type = 'email';
        emailInput.name = 'vat_email';
        emailInput.placeholder = 'Enter email';
        emailInput.autocomplete = 'email';
        emailInput.value = SHOP_CUSTOMER_EMAIL || '';
        emailWrapper.appendChild(emailInput);
        const span = document.createElement('span');
        span.textContent = 'Clear';
        span.style.cursor = 'pointer';
        span.addEventListener('click', function () {
          emailInput.value = '';
        });
        emailWrapper.appendChild(span);
        if (vatInputWrapper && vatInputWrapper.parentNode)
          vatInputWrapper.parentNode.insertBefore(emailWrapper, vatInputWrapper.nextSibling);
        else form.insertBefore(emailWrapper, form.querySelector('button') || null);
        console.info('VATValidator: injected email input into form');
      }
    } catch (e) {
      console.warn('VATValidator: error injecting email input', e);
    }

    const emitState = () => emit(getStateFromDom(form), form);
    const obs = new MutationObserver(debounce(emitState, 100));
    obs.observe(form, { attributes: true, childList: true, subtree: true, attributeFilter: ['style', 'class'] });

    const button = form.querySelector('button');
    if (button) {
      // Observe button attribute changes
      const bObs = new MutationObserver(debounce(emitState, 100));
      bObs.observe(button, { attributes: true, attributeFilter: ['class', 'disabled', 'style'] });
      form._vatValidatorObservers = form._vatValidatorObservers || [];
      form._vatValidatorObservers.push(bObs);

      // Bind validation guard on click and submit to ensure email is present and valid
      try {
        // Preserve original button label for restoration when we block action
        if (!button.dataset.vatOriginalText) button.dataset.vatOriginalText = button.textContent || '';

        // Click (capture phase) - run before other handlers to stop validation when email invalid
        button.addEventListener(
          'click',
          function (e) {
            try {
              const emailInput = form.querySelector('input[name="vat_email"]');
              const email = ((emailInput && emailInput.value) || '').trim();
              const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
              if (!isValidEmail) {
                e.preventDefault();
                e.stopImmediatePropagation();
                e.stopPropagation();

                console.warn('VATValidator: invalid email, blocking validation', { email });

                hideLoader();

                button.disabled = false;
                button.classList.remove('loading');
                if (button.dataset.vatOriginalText) button.textContent = button.dataset.vatOriginalText;

                const errorBanner = form.querySelector('.banner.error');
                if (errorBanner) {
                  errorBanner.textContent = 'Please enter a valid email';
                  errorBanner.style.display = 'block';
                  const successBanner = form.querySelector('.banner.success');
                  if (successBanner) successBanner.style.display = 'none';
                  setTimeout(() => {
                    errorBanner.style.display = 'none';
                  }, 5000);
                } else {
                  alert('Please enter a valid email');
                }

                if (emailInput) emailInput.focus();
                return;
              }
            } catch (err) {
              console.warn('VATValidator: error in click guard', err);
            }
          },
          true
        );

        // Also guard form submit (capture phase)
        form.addEventListener(
          'submit',
          function (e) {
            try {
              const emailInput = form.querySelector('input[name="vat_email"]');
              const email = ((emailInput && emailInput.value) || '').trim();
              const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
              if (!isValidEmail) {
                e.preventDefault();
                e.stopImmediatePropagation();
                e.stopPropagation();

                hideLoader();
                const errorBanner = form.querySelector('.banner.error');
                if (errorBanner) {
                  errorBanner.textContent = 'Please enter a valid email';
                  errorBanner.style.display = 'block';
                  const successBanner = form.querySelector('.banner.success');
                  if (successBanner) successBanner.style.display = 'none';
                  setTimeout(() => {
                    errorBanner.style.display = 'none';
                  }, 5000);
                } else {
                  alert('Please enter a valid email');
                }
                if (emailInput) emailInput.focus();
                return;
              }
            } catch (err) {
              console.warn('VATValidator: error in submit guard', err);
            }
          },
          true
        );
      } catch (e) {
        console.warn('VATValidator: could not bind click/submit guard to button/form', e);
      }
    }
    form._vatValidatorObservers.push(obs);

    // Initial state emit
    const initialState = getStateFromDom(form);
    console.info('VATValidator: initial state', initialState);
    emit(initialState, form);
  }

  // Watch document for the form to be injected/updated
  const bodyObserver = new MutationObserver(
    debounce(function () {
      const form = getVatForm();
      console.debug('VATValidator: bodyObserver sees form?', !!form);
      if (form) observeForm(form);
      if (form) emit(getStateFromDom(form), form);
    }, 150)
  );
  if (document && document.body) bodyObserver.observe(document.body, { childList: true, subtree: true });

  // API send with loader usage. Always calls backend when VAT validation succeeds.
  // Returns true if an API call was initiated, false if skipped (e.g., no email available).
  function sendSuccessToApi(detail, form) {
    const vat = detail.vatNumber || 'unknown';
    const email = detail.email || SHOP_CUSTOMER_EMAIL || null;
    const payload = { email, vat };

    console.info('VATValidator: preparing to send success to API', payload);

    if (!email) {
      console.warn('VATValidator: no email available, skipping API call');
      return false;
    }

    showLoader(); // keep loader visible while API request is in flight

    fetch(VAT_API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        const text = await res.text();
        console.info('VATValidator: API response', res.status, text);
        hideLoader();
        if (res.ok) {
          // Show success banner now that backend confirmed
          if (form) {
            // Setting the suppression flag prevents the DOM mutation caused by showing the
            // banner from being treated as a new validation event by observers.
            form._vatValidatorSuppressNextEmit = true;
            lastState = 'success';
            showSuccessBanner(form);
          }
        } else {
          console.warn('VATValidator: API returned error', res.status, text);
          // Show an in-form error message so user knows backend registration failed
          if (form) {
            // Prevent the error banner update from triggering a follow-up emit
            form._vatValidatorSuppressNextEmit = true;
            lastState = 'error';
            showErrorBanner(form, 'VAT validated but failed to register with our system. Please try again later.');
          }
        }
      })
      .catch((err) => {
        console.error('VATValidator: API request failed', err);
        hideLoader();
        if (form) {
          // Prevent the error banner update from triggering a follow-up emit
          form._vatValidatorSuppressNextEmit = true;
          lastState = 'error';
          showErrorBanner(form, 'VAT validated but failed to register with our system. Please try again later.');
        }
      })
      .finally(() => {
        hideLoader();
      });

    return true;
  }

  // Minimal public API
  window.VATValidator = window.VATValidator || {};
  window.VATValidator.onStateChange = function (cb) {
    callbacks.add(cb);
    console.debug('VATValidator: onStateChange registered', cb);
  };
  window.VATValidator.offStateChange = function (cb) {
    callbacks.delete(cb);
    console.debug('VATValidator: offStateChange removed', cb);
  };
  window.VATValidator.getState = function () {
    console.debug('VATValidator: getState ->', lastState);
    return lastState;
  };

  // Reflect URL param behaviour that some apps use (e.g., ?vat-validated=true)
  document.addEventListener('DOMContentLoaded', function () {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('vat-validated')) {
        const isValid = urlParams.get('vat-validated') === 'true';
        const state = isValid ? 'success' : 'error';
        console.info('VATValidator: url param detected, emitting', state);
        emit(state, getVatForm());
      } else {
        const form = getVatForm();
        if (form) emit(getStateFromDom(form), form);
      }
    } catch (e) {
      console.warn('VATValidator: error processing url param', e);
    }
  });
})();
